import { useCallback, useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { AlertCircle, ArrowRight, Check, Copy, Loader2, RefreshCw, ShieldCheck, Smartphone } from "lucide-react";
import { cn } from "./ui";
import { apiErrorText } from "@/lib/api";
import { onServerEvent } from "@/lib/events";
import { t } from "@/lib/i18n";
import { channelsApi, type Channel, type GatewayState } from "./inbox-api";
import { Hint, SideCard, Steps, SuccessBox, inputCls, type PanelProps, type WhatsLogin } from "./channel-wizard-kit";

/* Вход в обычный WhatsApp через шлюз (связанное устройство): QR-код, который обновляет сам WhatsApp,
   или 8-значный код привязки по номеру телефона. Канал создаётся на сервере сразу — состояние приходит
   живыми событиями и опросом раз в 2 секунды. */

const PREFIXES = ["+971", "+7", "+966", "+974", "+968", "+973", "+965", "+91", "+44", "+1"];

const TABS: { id: WhatsLogin; label: string }[] = [
  { id: "qr", label: t("Вход по QR-коду") },
  { id: "phone", label: t("Вход по номеру") },
];

export default function WhatsAppLogin({ onDone, existing }: PanelProps & { existing?: Channel | null }) {
  const [tab, setTab] = useState<WhatsLogin>("qr");
  const [channel, setChannel] = useState<Channel | null>(existing ?? null);
  const [state, setState] = useState<GatewayState | null>(existing?.state ?? null);
  const [error, setError] = useState("");
  const [prefix, setPrefix] = useState(PREFIXES[0]);
  const [phone, setPhone] = useState("");
  const [pairing, setPairing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const done = useRef(false);

  const digits = phone.replace(/\D/g, "");
  const fullNumber = digits ? `${prefix} ${phone.trim()}` : "";

  // создать канал и запустить сессию шлюза (или переподключить существующий)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ch = existing ? await channelsApi.reconnect(existing.id) : await channelsApi.create();
        if (cancelled) return;
        setChannel(ch);
        setState(ch.state);
      } catch (err) {
        if (!cancelled) setError(apiErrorText(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [existing]);

  // живое состояние: событие channel.state + опрос (QR меняется каждые ~20–60 с, его присылает WhatsApp)
  useEffect(() => {
    if (!channel) return undefined;
    const apply = (ch: Channel) => {
      setChannel(ch);
      if (ch.state) setState(ch.state);
    };
    const off = onServerEvent("channel.state", (data) => {
      const d = data as { id: string; status: Channel["status"]; phone: string | null; state: GatewayState };
      if (d.id !== channel.id) return;
      setState((prev) => ({ ...(prev ?? { state: "connecting" }), ...d.state }));
      setChannel((prev) => (prev ? { ...prev, status: d.status, phone: d.phone ?? prev.phone } : prev));
    });
    const poll = setInterval(() => {
      channelsApi.get(channel.id).then(apply).catch(() => undefined);
    }, 2000);
    return () => {
      off();
      clearInterval(poll);
    };
  }, [channel?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // таймер «QR живёт N секунд» — чисто визуальный, код обновляет сам WhatsApp
  useEffect(() => {
    setSeconds(60);
    const id = setInterval(() => setSeconds((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [state?.qr]);

  const finish = useCallback(
    (ch: Channel) => {
      if (done.current) return;
      done.current = true;
      onDone(ch.phone || ch.name || "WhatsApp", [
        { label: t("Тип"), value: t("WhatsApp · связанное устройство") },
        { label: t("Способ входа"), value: tab === "qr" ? t("QR-код") : t("Код по номеру") },
        { label: t("Номер"), value: ch.phone || fullNumber || "—" },
        { label: t("Профиль"), value: ch.accountName || "—" },
      ]);
    },
    [onDone, tab, fullNumber],
  );

  useEffect(() => {
    if (channel && (channel.status === "connected" || state?.state === "open")) finish(channel);
  }, [channel, state?.state, finish]);

  const requestCode = async () => {
    if (!channel || digits.length < 7) return;
    setPairing(true);
    setError("");
    try {
      const ch = await channelsApi.pair(channel.id, `${prefix}${digits}`);
      setChannel(ch);
      setState(ch.state);
    } catch (err) {
      setError(apiErrorText(err));
    } finally {
      setPairing(false);
    }
  };

  const reconnect = async () => {
    if (!channel) return;
    setError("");
    try {
      const ch = await channelsApi.reconnect(channel.id);
      setChannel(ch);
      setState(ch.state);
    } catch (err) {
      setError(apiErrorText(err));
    }
  };

  const copyQr = async () => {
    if (!state?.qr) return;
    try {
      await navigator.clipboard.writeText(state.qr);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const gw = state?.state;
  const waiting = !gw || gw === "starting" || gw === "connecting";
  const gatewayDown = gw === "gateway_down";
  const code = state?.pairingCode;

  const steps =
    tab === "qr"
      ? [t("Откройте WhatsApp на телефоне, который подключаем"), t("Меню → Связанные устройства → Привязка устройства"), t("Наведите камеру на код справа")]
      : code
        ? [t("Откройте WhatsApp на телефоне, который подключаем"), t("Связанные устройства → Привязка устройства → Привязать по номеру телефона"), t("Введите на телефоне код справа")]
        : [t("Введите номер телефона, на котором стоит WhatsApp"), t("Нажмите «Получить код»"), t("Введите код в WhatsApp на телефоне")];
  const activeNumber = tab === "qr" ? 3 : code ? 3 : 1;

  return (
    <div className="grid flex-1 overflow-y-auto lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div className="min-w-0 p-4 md:p-5">
        <div role="tablist" aria-label={t("Способ входа в WhatsApp")} className="flex gap-1 rounded-btn border border-border bg-background p-1">
          {TABS.map((tabItem) => (
            <button
              key={tabItem.id}
              type="button"
              role="tab"
              aria-selected={tab === tabItem.id}
              onClick={() => {
                setTab(tabItem.id);
                setError("");
              }}
              className={cn(
                "flex-1 cursor-pointer whitespace-nowrap rounded-btn px-3 py-1.5 text-[11.5px] font-semibold transition",
                tab === tabItem.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {tabItem.label}
            </button>
          ))}
        </div>

        <div className="mt-4">
          <Steps items={steps} active={activeNumber} />
        </div>

        {tab === "qr" ? (
          <div className="mt-4 rounded-btn border border-border bg-background p-3">
            <p className="text-[11.5px] leading-relaxed text-muted-foreground">
              {t("Код обновляется сам, пока вы его не отсканировали. Пароль от WhatsApp вводить не нужно — подтверждение приходит на телефон.")}
            </p>
            <p className="mt-2 text-[11.5px] leading-relaxed text-muted-foreground">
              {t("После подключения новые сообщения пойдут во «Входящие», подтянется переписка за последние {days} дней. Сообщения, отправленные с самого телефона, тоже попадут в Hub.", {
                days: channel?.settings.historyDays ?? 30,
              })}
            </p>
          </div>
        ) : (
          <div className="mt-4">
            <label htmlFor="wa-phone" className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t("Номер телефона с WhatsApp")}
            </label>
            <div className="mt-1.5 flex flex-col gap-2 sm:flex-row">
              <select value={prefix} onChange={(e) => setPrefix(e.target.value)} aria-label={t("Код страны")} className={cn(inputCls, "sm:w-[7.5rem] sm:flex-none cursor-pointer")}>
                {PREFIXES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
              <input
                id="wa-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/[^\d\s()-]/g, "").slice(0, 16))}
                placeholder="50 123 4567"
                inputMode="tel"
                autoComplete="off"
                disabled={Boolean(code)}
                className={cn(inputCls, "disabled:opacity-60")}
              />
              <button
                type="button"
                onClick={requestCode}
                disabled={digits.length < 7 || pairing || !channel || gatewayDown}
                className="inline-flex h-10 flex-none cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3.5 text-[12.5px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {pairing ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : null}
                {code ? t("Новый код") : pairing ? t("Запрашиваем…") : t("Получить код")}
              </button>
            </div>
            {code ? (
              <SuccessBox title={t("Код готов — введите его в WhatsApp на телефоне")}>
                <p className="text-[11px] text-muted-foreground">{t("Связанные устройства → Привязка устройства → «Привязать по номеру телефона». Код действует несколько минут.")}</p>
              </SuccessBox>
            ) : (
              <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
                {t("WhatsApp покажет на телефоне поле для кода. Если удобнее камера — выберите вход по QR-коду.")}
              </p>
            )}
          </div>
        )}

        {error ? (
          <p className="mt-3 flex items-start gap-1.5 rounded-btn border border-red-400/40 bg-red-500/10 px-2.5 py-2 text-[11.5px] leading-relaxed text-red-600">
            <AlertCircle className="mt-0.5 size-3.5 flex-none" aria-hidden />
            {error}
          </p>
        ) : null}
        {gatewayDown ? (
          <p className="mt-3 flex items-start gap-1.5 rounded-btn border border-red-400/40 bg-red-500/10 px-2.5 py-2 text-[11.5px] leading-relaxed text-red-600">
            <AlertCircle className="mt-0.5 size-3.5 flex-none" aria-hidden />
            {t("Шлюз WhatsApp не отвечает. Попробуйте через минуту или сообщите администратору.")}
          </p>
        ) : null}
        {gw === "logged_out" || gw === "closed" || gw === "failed" ? (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-btn border border-border bg-background px-2.5 py-2 text-[11.5px]">
            <span className="text-muted-foreground">{gw === "failed" ? t("Сессия не поднялась{error}.", { error: state?.error ? `: ${state.error}` : "" }) : t("Сессия завершена.")}</span>
            <button type="button" onClick={reconnect} className="inline-flex cursor-pointer items-center gap-1 font-semibold text-primary hover:brightness-110">
              <RefreshCw className="size-3" aria-hidden />
              {t("Попробовать снова")}
            </button>
          </div>
        ) : null}

        <Hint icon={<ShieldCheck className="size-3.5" />}>
          {t("Такой вход — как у Wazzup: номер работает как связанное устройство, писать клиенту первым можно. Массовые рассылки с него делать нельзя — для этого WABA.")}
        </Hint>
      </div>

      {/* правая колонка */}
      <div className="min-w-0 border-t border-border bg-background p-4 lg:border-s lg:border-t-0 md:p-5">
        {tab === "qr" ? (
          <div className="flex flex-col items-center">
            <div className="relative w-full max-w-[15rem] rounded-card border border-border bg-background p-3">
              <div className="grid aspect-square w-full place-items-center overflow-hidden rounded-btn bg-white p-2">
                {state?.qr ? (
                  <QRCodeSVG value={state.qr} size={220} level="L" bgColor="#ffffff" fgColor="#111111" className="size-full" />
                ) : (
                  <div className="text-center text-[#333]">
                    <Loader2 className="mx-auto size-6 animate-spin text-[#666]" aria-hidden />
                    <p className="mt-2 text-[11px]">{gatewayDown ? t("Шлюз недоступен") : gw === "pairing" ? t("Выбран вход по номеру") : t("Получаем код…")}</p>
                  </div>
                )}
              </div>
              {gw === "connecting" && channel?.status !== "qr" && state?.qr === null ? (
                <div className="absolute inset-3 grid place-items-center rounded-btn bg-background/90 backdrop-blur-sm">
                  <div className="text-center">
                    <span className="mx-auto grid size-11 place-items-center rounded-full bg-emerald-500 text-white" aria-hidden>
                      <Smartphone className="size-5" />
                    </span>
                    <p className="mt-2 text-[12px] font-semibold">{t("Подключаемся")}</p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">{t("Подтвердите на телефоне, если спросит")}</p>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="mt-3 flex w-full items-center justify-between gap-2 rounded-btn border border-border bg-background px-3 py-2">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
                <Loader2 className={cn("size-3 text-primary", (waiting || state?.qr) && "animate-spin")} aria-hidden />
                {state?.qr ? t("Ждём сканирование · {seconds} с", { seconds }) : waiting ? t("Соединяемся с WhatsApp…") : t("Код не выдан")}
              </span>
              <button
                type="button"
                onClick={copyQr}
                disabled={!state?.qr}
                title={t("Скопировать содержимое QR-кода, чтобы отправить коллеге и он открыл его у себя")}
                className="inline-flex flex-none cursor-pointer items-center gap-1 whitespace-nowrap rounded-btn px-1.5 py-1 text-[11px] font-semibold text-primary transition hover:bg-primary/10 disabled:opacity-40"
              >
                {copied ? <Check className="size-3" aria-hidden /> : <Copy className="size-3" aria-hidden />}
                {copied ? t("Скопировано") : t("Копировать код")}
              </button>
            </div>
            <p className="mt-2 text-center text-[10px] leading-relaxed text-muted-foreground">{t("Канал:")} {channel?.id.slice(0, 8) ?? t("создаётся…")}</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <SideCard title={t("Код для телефона")}>
              {code ? (
                <p className="mt-2 text-center font-mono text-[26px] font-bold tracking-[0.2em] text-foreground">{code}</p>
              ) : (
                <p className="mt-2 text-[12px] text-muted-foreground">{fullNumber || t("номер не указан")}</p>
              )}
              <div className="mt-2.5 flex flex-col gap-1.5">
                <span className={cn("flex items-center gap-1.5 text-[11px]", code ? "text-emerald-600" : "text-muted-foreground")}>
                  <Check className={cn("size-3", code ? "opacity-100" : "opacity-30")} strokeWidth={3} aria-hidden />
                  {t("Код получен")}
                </span>
                <span className={cn("flex items-center gap-1.5 text-[11px]", gw === "open" ? "text-emerald-600" : "text-muted-foreground")}>
                  <Check className={cn("size-3", gw === "open" ? "opacity-100" : "opacity-30")} strokeWidth={3} aria-hidden />
                  {t("Телефон подтвердил вход")}
                </span>
              </div>
            </SideCard>

            <SideCard title={t("Что будет после входа")}>
              <ul className="mt-2 flex flex-col gap-1.5">
                {[t("Диалоги появятся во «Входящие»"), t("Подтянутся имя профиля и номер"), t("Можно писать клиенту первым")].map((item) => (
                  <li key={item} className="flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground">
                    <Check className="mt-0.5 size-3 flex-none text-primary" strokeWidth={3} aria-hidden />
                    {item}
                  </li>
                ))}
              </ul>
            </SideCard>

            <div className="rounded-card border border-border bg-background p-3">
              <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground">
                <ArrowRight className="mt-0.5 size-3.5 flex-none text-primary" aria-hidden />
                {t("Нужны рассылки и зелёная галочка? Для этого подключают официальный WABA — отдельный тип аккаунта.")}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
